import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';
import { BookOpen, Search, Calendar, ChevronRight, X as IconX, Printer, FileSpreadsheet } from 'lucide-react';
import {
  salesOf, ROLES, findUserById, effectiveBranchesOf, getOrgSettings, ORG_TYPES
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { SideDrawer } from '../../components/SideDrawer';
import { FilterPopover } from '../../components/FilterPopover';
import { PrintBillModal } from '../../components/PrintBillModal';

export const SalesReportTab = ({ org, user, role, branchId }) => {
  const { version: dataVersion } = useSharedStore();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ branches: [], methods: [], dateRange: {} });
  const [view, setView] = useState(null);

  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id, dataVersion]);
  const branchesEnabled = useMemo(() => getOrgSettings(org.id).branchesEnabled, [org.id, dataVersion]);
  const isService = org.type === ORG_TYPES.SERVICE;

  const list = useMemo(() => {
    const scopedUserId = role === ROLES.ORG_USER ? user?.id : null;
    let all = salesOf(org.id, scopedUserId);
    if (role === ROLES.BRANCH_ADMIN && branchId) all = all.filter(b => b.locationId === branchId);
    return all
      .filter((b) => {
        if (filters.branches?.length > 0 && !filters.branches.includes(b.locationId)) return false;
        if (filters.methods?.length > 0 && !filters.methods.includes((b.method || '').toUpperCase())) return false;
        if (filters.dateRange?.from && b.date && b.date.slice(0, 10) < filters.dateRange.from) return false;
        if (filters.dateRange?.to && b.date && b.date.slice(0, 10) > filters.dateRange.to) return false;
        if (search.trim()) {
          const q = search.toLowerCase();
          if (!((b.id || '').toLowerCase().includes(q) || (b.customer || '').toLowerCase().includes(q))) return false;
        }
        return true;
      })
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [search, filters, org.id, role, user, branchId, dataVersion]);

  // Build ledger rows with running balance
  const ledgerRows = useMemo(() => {
    const res = []; let bal = 0; for(let i=0; i<list.length; i++){ bal += list[i].total; res.push({...list[i], runningBalance: bal, srNo: i+1}); } return res;
  }, [list]);

  const totals = useMemo(() => ({
    revenue: list.reduce((s, x) => s + x.total, 0),
    subtotal: list.reduce((s, x) => s + x.subtotal, 0),
    cgst: list.reduce((s, x) => s + (x.totalCgst || 0), 0),
    sgst: list.reduce((s, x) => s + (x.totalSgst || 0), 0),
    igst: list.reduce((s, x) => s + (x.totalIgst || 0), 0),
    bills: list.length,
    cash: list.filter(x => (x.method || '').toUpperCase() === 'CASH').reduce((s, x) => s + x.total, 0),
    card: list.filter(x => (x.method || '').toUpperCase() === 'CARD').reduce((s, x) => s + x.total, 0),
  }), [list]);

  const accent = org.theme.primary;

  // eslint-disable-next-line no-unused-vars
  const exportCsv = () => {
    const rows = [
      ['Sr.No', 'Date', 'Bill No', 'Particulars', 'Branch', 'Payment Mode', 'Subtotal (₹)', 'CGST (₹)', 'SGST (₹)', 'IGST (₹)', 'Total (₹)', 'Running Balance (₹)'],
      ...ledgerRows.map((b) => {
        const cashier = findUserById(b.userId);
        const branch = branches.find(br => br.id === b.locationId);
        return [
          b.srNo,
          b.date ? b.date.slice(0, 10) : '',
          b.id,
          `${b.customer || 'Walk-in'} | ${cashier?.name || ''}`,
          branch?.name || '',
          b.method || '',
          b.subtotal.toFixed(2),
          (b.totalCgst || 0).toFixed(2),
          (b.totalSgst || 0).toFixed(2),
          (b.totalIgst || 0).toFixed(2),
          b.total.toFixed(2),
          b.runningBalance.toFixed(2),
        ];
      }),
      ['', '', '', '', '', 'TOTAL', totals.subtotal.toFixed(2), totals.cgst.toFixed(2), totals.sgst.toFixed(2), totals.igst.toFixed(2), totals.revenue.toFixed(2), ''],
    ];
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `${isService ? 'booking-report' : 'sales-ledger'}-${org.name.replace(/\s+/g, '-')}.csv`;
    a.click();
  };

  const exportExcel = () => {
    const rows = [
      ['Sr.No', 'Date', 'Bill No', 'Particulars', 'Branch', 'Payment Mode', 'Subtotal (₹)', 'CGST (₹)', 'SGST (₹)', 'IGST (₹)', 'Total (₹)', 'Running Balance (₹)'],
      ...ledgerRows.map((b) => {
        const cashier = findUserById(b.userId);
        const branch = branches.find(br => br.id === b.locationId);
        return [
          b.srNo,
          b.date ? b.date.slice(0, 10) : '',
          b.id,
          `${b.customer || 'Walk-in'} | ${cashier?.name || ''}`,
          branch?.name || '',
          b.method || '',
          b.subtotal,
          (b.totalCgst || 0),
          (b.totalSgst || 0),
          (b.totalIgst || 0),
          b.total,
          b.runningBalance,
        ];
      }),
      ['', '', '', '', '', 'TOTAL', totals.subtotal, totals.cgst, totals.sgst, totals.igst, totals.revenue, ''],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, isService ? 'Booking Report' : 'Sales Ledger');
    XLSX.writeFile(workbook, `${isService ? 'booking-report' : 'sales-ledger'}-${org.name.replace(/\s+/g, '-')}.xlsx`);
  };

  return (
    <div className="flex flex-col gap-4">

      {/* Ledger Header Card & Filter Bar (Unified) */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-md shadow-sm">
        <div className="flex flex-row flex-wrap items-center justify-between gap-4 bg-white p-4 border-b border-[var(--theme-tertiary)] shrink-0 z-20 rounded-t-md">
          
          {/* SEARCH, FILTERS & EXPORT (Left) */}
          <div className="flex flex-1 min-w-[300px] items-center gap-3 flex-wrap">
            <div className="relative min-w-[160px] max-w-lg flex-1">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search bill / customer…"
                className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-md pl-9 pr-4 py-1.5 text-xs font-medium text-slate-800 outline-none transition-colors" />
            </div>

            <FilterPopover
              align="right"
              accent={accent}
              initialFilters={filters}
              onApply={setFilters}
              categories={[
                { id: 'dateRange', label: 'Date Range', type: 'date_range' },
                ...(!branchId && branchesEnabled && branches.length > 1 ? [{ id: 'branches', label: 'Branch', options: branches.map(b => ({ id: b.id, label: b.name })) }] : []),
                { id: 'methods', label: 'Payment Method', options: [{ id: 'CASH', label: 'Cash' }, { id: 'CARD', label: 'Card' }] }
              ]}
            />

            {(search || filters.dateRange?.from || filters.dateRange?.to || filters.branches?.length > 0 || filters.methods?.length > 0) && (
              <button type="button" onClick={() => { setSearch(''); setFilters({ branches: [], methods: [], dateRange: {} }); }}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-rose-50 text-rose-500 text-[11px] font-bold shrink-0 transition-colors hover:bg-rose-100">
                <IconX size={11} /> Clear
              </button>
            )}

            <button type="button" onClick={exportExcel} title="Export Excel"
              className="inline-flex items-center justify-center p-2.5 rounded-md bg-white border border-[var(--theme-tertiary)] text-slate-600 hover:border-[var(--theme-primary)] hover:text-[var(--theme-primary)] shadow-sm transition-colors shrink-0">
              <FileSpreadsheet size={15} />
            </button>
          </div>

          {/* TOTALS (Right) */}
          <div className="flex items-center gap-4 shrink-0 flex-wrap">
            <div className="flex items-center gap-5 px-2 py-2.5 shrink-0">
              <div className="text-left">
                <p className="text-slate-500 font-bold uppercase tracking-[0.12em] text-[10px]">Total Bills</p>
                <p className="font-extrabold text-[#0d0d0d] text-base">{totals.bills}</p>
              </div>
              <div className="w-px h-8 bg-[var(--theme-tertiary)]" />
              <div className="text-left">
                <p className="text-slate-500 font-bold uppercase tracking-[0.12em] text-[10px]">Total Revenue</p>
                <p className="font-extrabold text-base" style={{ color: accent }}>₹{totals.revenue.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Summary row */}
        <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-y md:divide-y-0 divide-[var(--theme-tertiary)] rounded-b-lg">
          {[
            { label: 'Subtotal', value: `₹${totals.subtotal.toFixed(2)}`, color: '#6366f1' },
            { label: 'CGST', value: `₹${totals.cgst.toFixed(2)}`, color: '#f59e0b' },
            { label: 'SGST', value: `₹${totals.sgst.toFixed(2)}`, color: '#f97316' },
            { label: 'IGST', value: `₹${totals.igst.toFixed(2)}`, color: '#ec4899' },
            { label: 'Cash / Card', value: `₹${totals.cash.toFixed(0)} / ₹${totals.card.toFixed(0)}`, color: '#10b981' },
          ].map(({ label, value, color }) => (
            <div key={label} className="px-5 py-3 flex flex-col gap-0.5">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</p>
              <p className="text-[13px] font-extrabold" style={{ color }}>{value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ledger Table */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-2xl overflow-hidden shadow-sm">
        {/* Ledger title bar */}
        <div className="px-5 py-2.5 border-b-2 border-[var(--theme-tertiary)] flex items-center justify-between bg-slate-50">
          <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-slate-500">
            {isService ? 'Bookings Account — Dr / Cr Ledger' : 'Sales Account — Dr / Cr Ledger'}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              {/* Opening balance row moved above headers */}
              <tr style={{ background: `${accent}10`, borderBottom: `1px solid ${accent}20` }}>
                <td colSpan={branchId ? 10 : 11} className="px-3 py-2 text-[11px] font-bold text-slate-500 uppercase tracking-[0.12em]">
                  Opening Balance
                </td>
                <td className="px-2 py-2 text-[12px] font-extrabold text-right text-slate-500">₹0.00</td>
              </tr>
              <tr className="bg-slate-50 border-b-2 border-slate-200">
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">Sr.</th>
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap">Date</th>
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap">Bill No.</th>
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">Particulars</th>
                {!branchId && <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">Branch</th>}
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500">Mode</th>
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500 text-right whitespace-nowrap">Subtotal</th>
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500 text-right">CGST</th>
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500 text-right">SGST</th>
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500 text-right">IGST</th>
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500 text-right whitespace-nowrap">Amount (Cr)</th>
                <th className="px-2 py-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-500 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>

              {ledgerRows.map((b, idx) => {
                const cashier = findUserById(b.userId);
                const branch = branches.find(br => br.id === b.locationId);
                const isEven = idx % 2 === 0;
                return (
                  <tr key={b.id}
                    className="border-b border-[var(--theme-tertiary)] transition-colors hover:bg-[var(--theme-primary-soft)]"
                    style={{ background: isEven ? '#fff' : '#fafafa' }}>
                    <td className="px-2 py-2.5 text-[11px] font-bold text-slate-300">{b.srNo}</td>
                    <td className="px-2 py-2.5 text-[12px] font-semibold text-slate-600 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        {b.date ? b.date.slice(0, 10) : '—'}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 text-[12px] font-extrabold text-[#0d0d0d] whitespace-nowrap">{b.id}</td>
                    <td className="px-2 py-2.5">
                      <p className="text-[12px] font-bold text-[#0d0d0d]">{b.customer || 'Walk-in Customer'}</p>
                      <p className="text-[10px] font-medium text-slate-400">By: {cashier?.name || '—'}</p>
                    </td>
                    {!branchId && (
                      <td className="px-2 py-2.5 text-[11px] text-slate-500">{branch?.name || '—'}</td>
                    )}
                    <td className="px-2 py-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-600">
                        {b.method}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 text-[12px] text-slate-600 text-right whitespace-nowrap">₹{b.subtotal.toFixed(2)}</td>
                    <td className="px-2 py-2.5 text-[12px] text-slate-500 text-right">₹{(b.totalCgst || 0).toFixed(2)}</td>
                    <td className="px-2 py-2.5 text-[12px] text-slate-500 text-right">₹{(b.totalSgst || 0).toFixed(2)}</td>
                    <td className="px-2 py-2.5 text-[12px] text-slate-500 text-right">₹{(b.totalIgst || 0).toFixed(2)}</td>
                    <td className="px-2 py-2.5 text-[13px] font-extrabold text-right whitespace-nowrap" style={{ color: '#10b981' }}>
                      ₹{b.total.toFixed(2)}
                    </td>
                    <td className="px-2 py-2.5 text-[12px] font-extrabold text-right" style={{ color: accent }}>
                      ₹{b.runningBalance.toFixed(2)}
                    </td>
                  </tr>
                );
              })}

              {ledgerRows.length === 0 && (
                <tr>
                  <td colSpan={branchId ? 11 : 12} className="px-5 py-20 text-center">
                    <BookOpen size={32} className="mx-auto text-slate-200 mb-3" />
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-300">No entries found</p>
                  </td>
                </tr>
              )}
            </tbody>

            {/* Closing balance / totals footer */}
            {ledgerRows.length > 0 && (
              <tfoot>
                <tr style={{ background: `${accent}10`, borderTop: `2px solid ${accent}30` }}>
                  <td colSpan={branchId ? 5 : 6} className="px-2 py-3 text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-500">
                    Closing Balance
                  </td>
                  <td className="px-2 py-3 text-right text-[12px] font-extrabold text-[#0d0d0d] whitespace-nowrap">₹{totals.subtotal.toFixed(2)}</td>
                  <td className="px-2 py-3 text-right text-[12px] font-extrabold text-[#0d0d0d]">₹{totals.cgst.toFixed(2)}</td>
                  <td className="px-2 py-3 text-right text-[12px] font-extrabold text-[#0d0d0d]">₹{totals.sgst.toFixed(2)}</td>
                  <td className="px-2 py-3 text-right text-[12px] font-extrabold text-[#0d0d0d]">₹{totals.igst.toFixed(2)}</td>
                  <td className="px-2 py-3 text-right text-[13px] font-extrabold whitespace-nowrap" style={{ color: '#10b981' }}>₹{totals.revenue.toFixed(2)}</td>
                  <td className="px-2 py-3 text-right text-[13px] font-extrabold whitespace-nowrap" style={{ color: accent }}>₹{totals.revenue.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      {/* Bill detail modal */}
      {view && (
        <PrintBillModal bill={view} org={org} onClose={() => setView(null)} />
      )}
    </div>
  );
};

SalesReportTab.propTypes = {
  org: PropTypes.object.isRequired,
  user: PropTypes.object,
  role: PropTypes.string,
  branchId: PropTypes.string,
};
